import { useEffect } from "react";
import { getSignalRConnection } from "@/lib/signalr/signalr-connection";
import { HubConnectionState } from "@microsoft/signalr";
import {
  SocketNotificationPayload,
  NotificationUserType,
} from "@/interface/notification.types";
import { useQueryClient } from "@tanstack/react-query";
import { useChatUnreadStore } from "@/store/chat-unread-store";

/**
 * Hook to test and manage the SignalR connection lifecycle
 */
export const useNotificationSocket = (
  isLoggedIn: boolean | null,
  userType?: "Homeowner" | "Contractor"
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // If we don't know the login status yet (null), do nothing and wait
    if (isLoggedIn === null) return;

    // Acquire the connection inside the effect, never in the render body:
    // effects don't run during SSR, while a render-body call constructs a
    // SignalR HttpConnection on the server, where SignalR's dynamic
    // require("ws") crashes with MODULE_NOT_FOUND on Amplify/standalone
    // (those shims aren't traced into the deployed bundle) — 500ing every
    // dynamically-rendered page that mounts this hook.
    const connection = getSignalRConnection();

    const manageConnection = async () => {
      if (isLoggedIn) {
        // Only start if disconnected
        if (connection.state === HubConnectionState.Disconnected) {
          try {
            await connection.start();
            console.log("SignalR Connected: Notification Hub");
          } catch (err) {
            console.error("SignalR Connection Error: ", err);
          }
        }
      } else if (
        connection.state === HubConnectionState.Connected ||
        connection.state === HubConnectionState.Connecting
      ) {
        // Stop if connected and user is logged out
        try {
          await connection.stop();
          console.log("SignalR Disconnected: Notification Hub");
        } catch (err) {
          console.error("SignalR Disconnect Error: ", err);
        }
      }
    };

    manageConnection();
    
    // If we don't have a userType yet, we can't properly filter notifications
    if (!userType) return;
    // Listener for real-time notifications (Bell Icon)
    const handleReceiveNotification = (payload: SocketNotificationPayload) => {
      // 🚨 CRITICAL: Check Type - Do not process messages in the general notification handler
      const hasUnreadMessageKey = payload.unReadCount !== undefined || payload.unreadCount !== undefined;
      if (payload.notificationType === "MessageReceived" || (hasUnreadMessageKey && payload.notificationCount === undefined)) {
        console.log("⏩ Skipping general handler for message-like payload");
        return;
      }

      // Verify that the notification is meant for the current user type
      const numericUserType =
        userType === "Homeowner"
          ? NotificationUserType.Homeowner
          : NotificationUserType.Contractor;

      if (payload.userType && payload.userType !== numericUserType) return;

      // Update the React Query cache for general unread notification count
      if (typeof payload.notificationCount === "number") {
        console.log("🔔 [Socket] Updating BELL Count to:", payload.notificationCount);
        queryClient.setQueryData(
          ["unreadNotificationCount"],
          (oldData: any) => ({
            ...oldData,
            data: {
              ...oldData?.data,
              count: payload.notificationCount,
            },
          })
        );
      }
    };

    // Listener for message notifications (Chat Icon)
    const handleReceiveMessageNotification = (payload: SocketNotificationPayload) => {
      // 🚨 CRITICAL: Check Type - Ensure we only process messages here
      const unreadCountValue = payload.unReadCount ?? payload.unreadCount;
      if (payload.notificationType !== "MessageReceived" && unreadCountValue === undefined) {
        console.log("⏩ Skipping message handler for non-message payload");
        return;
      }

      // 🛡️ FRONTEND FIX: Store-Aware Surgical Suppression
      // We calculate the badge count based on a comparison between the server total 
      // and our local knowledge of what is 'Read'.
      const store = useChatUnreadStore.getState();
      const activeProposalId = store.activeProposalId;
      const unreadProposalIds = store.unreadProposalIds;
      
      // 1. Extract ProposalId robustly (from payload or nested data)
      let incomingProposalId = payload.proposalId;
      if (!incomingProposalId && payload.data) {
        try {
          const parsed = typeof payload.data === 'string' ? JSON.parse(payload.data) : payload.data;
          incomingProposalId = parsed.ProposalId || parsed.proposalId;
        } catch (e) { /* ignore */ }
      }

      const isForActiveChat = !!(incomingProposalId && incomingProposalId === activeProposalId);
      
      let serverUnreadCount = unreadCountValue ?? 0;
      let badgeCount = serverUnreadCount;

      /**
       * 🏆 SENIOR LOGIC: The Discrepancy Matcher
       * 
       * Case A: Message is for Active Chat -> Acknowledge and Hide (-1)
       * Case B: Message is for Someone Else -> Show correctly
       * 
       * To handle server lag (stale counts), we only subtract if:
       * - We are in an active chat
       * - The server total is higher than our local total of UNREAD conversations
       */
      const localUnreadCount = unreadProposalIds.length;
      
      if (activeProposalId) {
        // If the server tells us we have more unreads than our local 'unread' list,
        // it means the server is likely still counting the active chat as unread.
        if (serverUnreadCount > localUnreadCount) {
          badgeCount = serverUnreadCount - 1;
        }
      }

      // Immediate Acknowledgment for the active chat
      if (isForActiveChat && payload.id) {
        console.log("🙈 Suppressing & Acknowledging active chat notification:", incomingProposalId);
        import("@/services/notification.service").then((m) => {
          m.markAsRead(payload.id).catch(() => {});
        });
      }

      const numericUserType =
        userType === "Homeowner"
          ? NotificationUserType.Homeowner
          : NotificationUserType.Contractor;

      if (payload.userType && payload.userType !== numericUserType) return;

      // Update the React Query cache for unread message notification count
      if (typeof unreadCountValue === "number") {
        console.log(`💬 [Socket] Badge Update: Server(${serverUnreadCount}) -> Display(${badgeCount}) | Active: ${activeProposalId || 'none'}`);
        queryClient.setQueryData(
          ["unreadMessageNotificationCount"],
          (oldData: any) => ({
            ...oldData,
            data: {
              ...oldData?.data,
              count: badgeCount,
            },
          })
        );
      }
    };

    connection.on("ReceiveNotification", handleReceiveNotification);
    connection.on("ReceiveMessageNotification", handleReceiveMessageNotification);
    
    // Optional: Review reminders also come through here
    connection.on("ReceiveReviewReminder", (payload) => {
      // Usually these update the general notification count
      handleReceiveNotification(payload);
    });

    // Log connection state changes for debugging
    connection.onreconnecting((error) => {
      console.warn("SignalR Reconnecting: ", error);
    });

    connection.onreconnected((connectionId) => {
      console.log("SignalR Reconnected. ID: ", connectionId);
    });

    connection.onclose((error) => {
      console.error("SignalR Connection Closed: ", error);
    });

    return () => {
      // Clean up the listeners when the hook unmounts or dependencies change
      connection.off("ReceiveNotification", handleReceiveNotification);
      connection.off("ReceiveMessageNotification", handleReceiveMessageNotification);
      connection.off("ReceiveReviewReminder");
    };
    // connection is a module-level singleton, so it's intentionally not a
    // dependency — its identity never changes across renders.
  }, [isLoggedIn, userType, queryClient]);
};
